// Scan feedback sounds — an HTMLAudioElement playing a GENERATED chirp WAV.
//
// WHY A MEDIA ELEMENT AND NOT WEB AUDIO: iOS mutes Web Audio output when the phone's RINGER/SILENT
// switch is on silent — which is how most iPhones live — while MEDIA playback (<audio>/<video>) is
// exempt (that is why videos have sound on a muted phone). The Web-Audio chirp shipped twice and
// both times a real iPhone reported "scanned, but silent" (2026-08-17): the second time even with a
// correct in-gesture unlock, which is what points to the silent switch. A media element is the only
// channel that plays regardless.
//
// UNLOCK: iOS allows programmatic .play() on an element only after that SAME element has played once
// inside a user gesture. `unlockScanAudio()` is called from the ⌖ tap and plays the chirp audibly —
// deliberately: it is the "scanner ready" cue AND a self-test (if the ready-chirp is silent, the
// phone's media volume is down and no code can help). One unlock covers the session.
//
// TWO TONES, ONE ELEMENT: iOS re-requires a gesture per element, so the miss buzz cannot be a second
// element. Instead the SAME chirp is replayed at playbackRate 0.32 with pitch-preservation off — a
// low, longer buzz from the same unlocked element.

/** 16-bit mono PCM WAV of a short scanner chirp, as a data URI. Pure; exported for tests. */
export function buildChirpWavDataUri({ freq = 1870, seconds = 0.09, rate = 22050 } = {}) {
  const n = Math.floor(rate * seconds);
  const bytes = new Uint8Array(44 + n * 2);
  const dv = new DataView(bytes.buffer);
  const w = (off, s) => {
    for (let i = 0; i < s.length; i++) bytes[off + i] = s.charCodeAt(i);
  };
  w(0, "RIFF");
  dv.setUint32(4, 36 + n * 2, true);
  w(8, "WAVE");
  w(12, "fmt ");
  dv.setUint32(16, 16, true); // PCM chunk size
  dv.setUint16(20, 1, true); // PCM
  dv.setUint16(22, 1, true); // mono
  dv.setUint32(24, rate, true);
  dv.setUint32(28, rate * 2, true); // byte rate
  dv.setUint16(32, 2, true); // block align
  dv.setUint16(34, 16, true); // bits
  w(36, "data");
  dv.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const t = i / rate;
    const env = Math.min(1, t / 0.008) * Math.exp(-t * 26); // fast attack, natural decay
    dv.setInt16(44 + i * 2, Math.round(Math.sin(2 * Math.PI * freq * t) * env * 0.6 * 32767), true);
  }
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return "data:audio/wav;base64," + btoa(bin);
}

let el = null;
let unlocked = false;

function ensureEl() {
  if (!el) {
    el = new Audio(buildChirpWavDataUri());
    el.preload = "auto";
  }
  return el;
}

/**
 * Call from inside a click/tap handler (the ⌖ camera button). Plays the ready-chirp audibly, which
 * is what unlocks this element for the programmatic plays the detection loop needs. Idempotent —
 * one ready-chirp per session.
 */
export function unlockScanAudio() {
  try {
    const a = ensureEl();
    if (unlocked) return;
    a.currentTime = 0;
    a.playbackRate = 1;
    const p = a.play();
    if (p && p.then) p.then(() => { unlocked = true; }).catch(() => {});
    else unlocked = true;
  } catch {
    /* no audio — scanning still works silently */
  }
}

/** The scanner chirp: the WAV at full rate for a hit; slowed to a low buzz for a miss. */
export function scanChirp(ok) {
  try {
    const a = ensureEl();
    a.pause();
    a.currentTime = 0;
    // preservesPitch OFF so the slow-down actually lowers the pitch (that IS the miss sound).
    if ("preservesPitch" in a) a.preservesPitch = false;
    else if ("webkitPreservesPitch" in a) a.webkitPreservesPitch = false;
    a.playbackRate = ok ? 1 : 0.32;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  } catch {
    /* sound is a nicety — never let it break a scan */
  }
}
